import { ApplicationState, OddsDisplayPreference } from "@ppb/tbd-store";
import { createSelector } from "reselect";
import { ExtraWallets } from "@ppb/tbd-store/state/entities/extra-wallet/ExtraWallet.types";
import { ExtraWalletCards } from "@ppb/tbd-store/state/layout/cards/Card.types";
import { WalletTypes } from "@ppb/tbd-store/clients/catalogue/catalogue-response-types";
import {
  WalletToken,
  WalletsTokensMap,
  createGetCombinationEligibleGenerosityWalletsSelector,
} from "@ppb/tbd-store/state/betting/sportsbook-betting/sportsbook-betting-selectors";
import { getGenerosityWalletSelectedCombinationId } from "@ppb/tbd-store/state/generosity-wallet/generosity-wallet-selectors";
import URN from "@ppb/tbd-store/state/layout/URN";
import type { TranslationKey } from "../translations/keys";
import { i18n } from "./i18n";
import { formatOdds } from "@ppb/formatters";

export type OptionWallet = {
  isSelected: boolean;
  isDisabled: boolean;
} & WalletToken;

export type OptionWallets = {
  [id: string]: OptionWallet;
};

export type GenerosityCardsURNByType = {
  [key: string]: URN[];
};

export const sumWalletsAmounts = (value1 = 0, value2 = 0): number =>
  value1 || value2 ? (value1 * 100 + value2 * 100) / 100 : 0;

/**
 * Formats decimal odds to a strict 2 decimal places.
 * e.g. 2 → "2.00", 2.5 → "2.50", 2.75 → "2.75"
 */
export const formatDecimalOdds = (value: number): string => value.toFixed(2);

/**
 * Formats fixed odds based on the user's display preference.
 * For decimal: shows a strict 2 decimal places.
 * For default and fractional: uses the tbd standard for fractional odds.
 */
export const formatFixedOdds = (decimalOdds: number, oddsDisplayPreference: OddsDisplayPreference): string =>
  oddsDisplayPreference === OddsDisplayPreference.Decimal
    ? formatDecimalOdds(decimalOdds)
    : formatOdds({ decimal: decimalOdds }, OddsDisplayPreference.Fractional, false);

export enum PebbleFilterOptions {
  All = "ALL",
  AccaInsuranceToken = WalletTypes.AccaInsuranceToken,
  MoneyBackToken = WalletTypes.MoneyBackToken,
  FreeBets = WalletTypes.BonusCash,
  PriceBoostToken = WalletTypes.PriceBoostToken,
  GhostLegToken = WalletTypes.GhostLegToken,
}

export const MAP_PEBBLE_OPTION_TO_GA_LABEL: Record<PebbleFilterOptions, string> = {
  [PebbleFilterOptions.All]: "all",
  [PebbleFilterOptions.FreeBets]: "free bets",
  [PebbleFilterOptions.AccaInsuranceToken]: "money back",
  [PebbleFilterOptions.MoneyBackToken]: "money back",
  [PebbleFilterOptions.PriceBoostToken]: "boost",
  [PebbleFilterOptions.GhostLegToken]: "ghost leg",
};

const getPlaceWithEnding = (place: number) => {
  const keyMap: Record<number, keyof TranslationKey> = {
    2: "I18N.MONEY_BACK_REWARD.PLACED_SECOND",
    3: "I18N.MONEY_BACK_REWARD.PLACED_THIRD",
  };

  const key = keyMap[place] ?? "I18N.MONEY_BACK_REWARD.PLACED_OTHER";
  return `${place}${i18n({ key })}`;
};

export const getPlacesListed = (maxFinPos?: number | null) => {
  if (!maxFinPos || maxFinPos < 2) {
    return "";
  }

  const normalSeparator = ", ";
  const finalSeparator = ` ${i18n({ key: "I18N.MONEY_BACK_REWARD.SEPARATOR" })} `;

  const placesLabels = Array.from({ length: maxFinPos - 1 }, (_, idx) => getPlaceWithEnding(idx + 2));

  const places = placesLabels.map((placeLabel, idx) => {
    if (idx === 0) {
      return placeLabel;
    }

    const isLastLabel = idx === placesLabels.length - 1;
    const labelSeparator = isLastLabel ? finalSeparator : normalSeparator;

    return `${labelSeparator}${placeLabel}`;
  });

  return places.join("");
};

export const pickMoneyBackAlertMessage = ({
  maxFinPos,
  formattedAmount,
  isRefundWithCash,
}: {
  maxFinPos?: number | null;
  formattedAmount: string;
  isRefundWithCash?: boolean;
}) => {
  if (maxFinPos) {
    const placesListed = getPlacesListed(maxFinPos);
    return i18n({
      key: isRefundWithCash ? "I18N.MONEY_BACK_REWARD.RUN_PLACES.CASH" : "I18N.MONEY_BACK_REWARD.RUN_PLACES",
      interpolationValues: { places: placesListed, amount: formattedAmount },
    });
  }

  return i18n({
    key: isRefundWithCash ? "I18N.MONEY_BACK_REWARD.HORSE_LOSES.CASH" : "I18N.MONEY_BACK_REWARD.HORSE_LOSES",
    interpolationValues: { amount: formattedAmount },
  });
};

const mapWalletTypeToPebble = {
  [WalletTypes.BonusCash]: PebbleFilterOptions.FreeBets,
  [WalletTypes.AccaInsuranceToken]: PebbleFilterOptions.AccaInsuranceToken,
  [WalletTypes.MoneyBackToken]: PebbleFilterOptions.AccaInsuranceToken,
  [WalletTypes.PriceBoostToken]: PebbleFilterOptions.PriceBoostToken,
  [WalletTypes.GhostLegToken]: PebbleFilterOptions.GhostLegToken,
};

export const createGenerosityCardsURNByTypeSelector = () => {
  const getCombinationEligibleGenerosityWallets = createGetCombinationEligibleGenerosityWalletsSelector();

  return createSelector(
    [
      (state: ApplicationState): ExtraWallets => state.entities.extraWallets,
      (state: ApplicationState): ExtraWalletCards => state.layouts.cards.extrawallet,
      (state: ApplicationState) => getCombinationEligibleGenerosityWallets(state),
      getGenerosityWalletSelectedCombinationId,
    ],
    (
      extraWallets: ExtraWallets,
      extraWalletCards: ExtraWalletCards,
      eligibleGenerosityWallets: WalletsTokensMap,
      selectedCombinationId: string | undefined,
    ): GenerosityCardsURNByType => {
      const isBetslipMode = !!selectedCombinationId;

      return Object.values(extraWalletCards).reduce<GenerosityCardsURNByType>((acc, { urn, extraWalletURN }) => {
        const { walletType } = extraWallets[extraWalletURN];

        if (isBetslipMode) {
          const isGenerosityWalletEligible = Object.keys(eligibleGenerosityWallets).some((walletId) =>
            urn.includes(walletId),
          );

          if (!isGenerosityWalletEligible) return acc;
        }

        const mappedWalletType = walletType && mapWalletTypeToPebble[walletType];

        if (mappedWalletType) {
          acc[mappedWalletType] = [...(acc[mappedWalletType] ?? []), urn];
        }

        return acc;
      }, {});
    },
  );
};

export const updateOptionWallets = (
  prev: OptionWallets,
  walletId: string,
  isSelected: boolean,
  selectedCombinationId: string | undefined,
): OptionWallets => {
  const { type } = prev[walletId];

  if (!isSelected)
    return {
      ...prev,
      [walletId]: { ...prev[walletId], isSelected, combinationId: undefined },
    };

  if (type === WalletTypes.BonusCash) {
    const updatedWallets = Object.fromEntries(
      Object.entries(prev).map(([id, wallet]) => [
        id,
        wallet.type === WalletTypes.BonusCash
          ? wallet
          : {
              ...wallet,
              isSelected: wallet.isDisabled ? wallet.isSelected : false,
              combinationId: wallet.isDisabled ? wallet.combinationId : undefined,
            },
      ]),
    );
    return {
      ...updatedWallets,
      [walletId]: { ...prev[walletId], isSelected, combinationId: selectedCombinationId },
    };
  }

  const updatedWallets = Object.fromEntries(
    Object.entries(prev).map(([id, wallet]) => [
      id,
      {
        ...wallet,
        isSelected: wallet.isDisabled ? wallet.isSelected : false,
        combinationId: wallet.isDisabled ? wallet.combinationId : undefined,
      },
    ]),
  );

  return {
    ...updatedWallets,
    [walletId]: { ...prev[walletId], isSelected, combinationId: selectedCombinationId },
  };
};
